import { useEffect, useState } from 'react';
import type { ChapterContentEntry } from './types';

/**
 * Per-CHAPTER content. Two sources, in priority order:
 *
 * 1. **Inlined** — `window.__ACES_CACHE__[id]`, baked into the prerendered HTML
 *    by scripts/prerender.mjs. When present the content is available
 *    SYNCHRONOUSLY on first render, so the page never flashes empty and never
 *    reflows (which previously threw off the chapter-nav scroll targets).
 * 2. **Fetched** — `/data/<id>.json` (~5–10 KB), for client-side route changes
 *    and any page that wasn't prerendered.
 *
 * Generated by scripts/split-chapter-content.mjs.
 */

type Cache = Record<string, ChapterContentEntry>;
function inlineCache(): Cache {
  if (typeof window === 'undefined') return {};
  return ((window as unknown as { __ACES_CACHE__?: Cache }).__ACES_CACHE__ ??= {});
}
function getInlined(id: string): ChapterContentEntry | null {
  return inlineCache()[id] ?? null;
}

const promises = new Map<string, Promise<ChapterContentEntry | null>>();
function loadChapter(id: string): Promise<ChapterContentEntry | null> {
  const inlined = getInlined(id);
  if (inlined) return Promise.resolve(inlined);
  if (!promises.has(id)) {
    promises.set(
      id,
      fetch(`/data/${encodeURIComponent(id)}.json`)
        .then((r) => (r.ok ? (r.json() as Promise<ChapterContentEntry>) : null))
        .then((entry) => {
          if (entry) inlineCache()[id] = entry; // expose so prerender can capture it
          return entry;
        })
        .catch(() => null),
    );
  }
  return promises.get(id)!;
}

export type ContentState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; entry: ChapterContentEntry | null };

export function useChapterContent(chapterId: string | undefined): ContentState {
  // Synchronous initial state: if the chapter is inlined (prerendered page),
  // start 'ready' so the very first render already has the content.
  const [state, setState] = useState<ContentState>(() => {
    if (!chapterId) return { status: 'idle' };
    const inlined = getInlined(chapterId);
    return inlined ? { status: 'ready', entry: inlined } : { status: 'loading' };
  });

  useEffect(() => {
    if (!chapterId) {
      setState({ status: 'idle' });
      return;
    }
    const inlined = getInlined(chapterId);
    if (inlined) {
      setState({ status: 'ready', entry: inlined });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    loadChapter(chapterId).then((entry) => {
      if (!cancelled) setState({ status: 'ready', entry });
    });
    return () => {
      cancelled = true;
    };
  }, [chapterId]);

  return state;
}
